import React, { useEffect, useState } from "react";
import {
  FaBirthdayCake,
  FaCalendarAlt,
  FaExchangeAlt,
  FaFilter,
  FaLayerGroup,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaUserCheck,
  FaUserTimes,
} from "react-icons/fa";

import { getGroupName, getLocalDateInput } from "../utils/attendanceUtils";

function FilterChip({ active, onClick, label, icon, tone = "sky" }) {
  const activeClass =
    tone === "emerald"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
      : tone === "rose"
        ? "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-200"
        : tone === "pink"
          ? "bg-pink-100 text-pink-700 dark:bg-pink-500/10 dark:text-pink-200"
          : "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex min-h-10 items-center gap-2 rounded-2xl px-3 py-2 text-xs font-bold transition ${
        active
          ? activeClass
          : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-white/60 dark:hover:bg-white/15"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function AttendanceFiltersCard({
  loading,
  filters,
  setFilters,
  selectedGroup,
  onRefresh,
  onChangeGroup,
}) {
  const [localSearch, setLocalSearch] = useState(filters.search || "");

  useEffect(() => {
    setLocalSearch(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((previous) => ({
        ...previous,
        search: localSearch,
      }));
    }, 250);

    return () => clearTimeout(timer);
  }, [localSearch, setFilters]);

  const resetFilters = () => {
    setLocalSearch("");

    setFilters((previous) => ({
      ...previous,
      search: "",
      selectedDate: getLocalDateInput(),
      onlyPresent: false,
      onlyAbsent: false,
      onlyBirthday: false,
      sortAssiduity: false,
    }));
  };

  return (
    <section className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
            <FaFilter />
            Appel en cours
          </div>

          <h2 className="mt-3 font-titre text-xl font-bold text-slate-950 dark:text-white">
            {getGroupName(selectedGroup)}
          </h2>

          <p className="mt-2 text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
            Recherche, date et filtres rapides pour l’appel du cours.
          </p>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:border-sky-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/70"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>

          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:border-red-200 hover:text-red-600 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/70"
          >
            <FaTimes />
            Reset
          </button>

          <button
            type="button"
            onClick={onChangeGroup}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-blue-700 px-3 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-blue-800"
          >
            <FaExchangeAlt />
            Changer
          </button>
        </div>
      </div>

      <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-700 text-white">
            <FaLayerGroup />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              Groupe sélectionné
            </p>

            <p className="font-titre text-xl font-bold text-slate-950 dark:text-white">
              {getGroupName(selectedGroup)}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-[1.4fr_0.6fr]">
        <label className="relative block">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="search"
            value={localSearch}
            onChange={(event) => setLocalSearch(event.target.value)}
            placeholder="Nom, prénom, licence..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
          />
        </label>

        <label className="relative block">
          <FaCalendarAlt className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="date"
            value={filters.selectedDate}
            onChange={(event) =>
              setFilters((previous) => ({
                ...previous,
                selectedDate: event.target.value,
              }))
            }
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <FilterChip
          active={filters.sortAssiduity}
          label="Tri assiduité"
          onClick={() =>
            setFilters((previous) => ({
              ...previous,
              sortAssiduity: !previous.sortAssiduity,
            }))
          }
        />

        <FilterChip
          active={filters.onlyPresent}
          label="Présents"
          icon={<FaUserCheck />}
          tone="emerald"
          onClick={() =>
            setFilters((previous) => ({
              ...previous,
              onlyPresent: !previous.onlyPresent,
              onlyAbsent: false,
            }))
          }
        />

        <FilterChip
          active={filters.onlyAbsent}
          label="Absents"
          icon={<FaUserTimes />}
          tone="rose"
          onClick={() =>
            setFilters((previous) => ({
              ...previous,
              onlyAbsent: !previous.onlyAbsent,
              onlyPresent: false,
            }))
          }
        />

        <FilterChip
          active={filters.onlyBirthday}
          label="Anniversaires"
          icon={<FaBirthdayCake />}
          tone="pink"
          onClick={() =>
            setFilters((previous) => ({
              ...previous,
              onlyBirthday: !previous.onlyBirthday,
            }))
          }
        />
      </div>
    </section>
  );
}

export default AttendanceFiltersCard;